<template>
    <CContainer fluid>
        <CRow>
            <CCol>
                <SelectAsync
                    :options="groupableFields"
                    :value.sync="selectedField"
                    :placeholder="$t('Select a field')"
                    optionsValue="key"
                    optionsLabel="label"
                >
                </SelectAsync>
            </CCol>
        </CRow>
        <CRow>
            <CCol>
                <vue-query-builder
                    :rules="rules"
                    v-model="query"
                    :labels="defaultLabels"
                    :maxDepth="1"
                >
                    <template v-slot:default="slotProps">
                        <CCSubQueryBuilderGroup
                            v-bind="slotProps"
                            :query.sync="query"
                            :limitable="false"
                        />
                    </template>
                </vue-query-builder>
            </CCol>
        </CRow>
    </CContainer>
</template>

<script>
/** Mixins */

import { validationMixin } from "vuelidate";
import baseMixin from "~/mixins/base";
import VueQueryBuilder from "vue-query-builder";
import CCSubQueryBuilderGroup from "./CCSubQueryBuilderGroup";
import SelectAsync from "£lement/SelectAsync";
import { generateShortUniqueId } from "~/utils";
import { SEGMENTATIONS_DELIVERY_CAMPAINGS_DELIVERIES_QUERY_MODEL_ID } from "../config/consts";

const defaultRuleType = "text";
const defaultOperators = [
    "equal",
    "like",
    {
        label: "Not like",
        value: "notLike",
    },
    {
        label: "empty",
        value: "empty",
        type: "radio",
        choices: [
            {
                label: "Yes",
                value: true,
            },
            {
                label: "No",
                value: false,
            },
        ],
    },
    {
        label: "Between",
        value: "between",
        type: "between",
    },
    "in",
    "notIn",
    {
        label: "In file",
        value: "inFile",
        type: "inFile",
    },
    {
        label: "Not in file",
        value: "notInFile",
        type: "notInFile",
    },
    {
        label: "Greater than",
        value: "greaterThan",
    },
    "regExp",
    "startBy",
    "notStartBy",
];

const defaultRules = [
    {
        type: "text",
        id: "LeadId",
        label: "LeadId",
        operators: defaultOperators,
    },
    {
        type: "text",
        id: "ProviderName",
        label: "ProviderName",
        operators: ["contains", "equal", "like"],
    },
    {
        type: "text",
        id: "Email",
        label: "Email",
        operators: defaultOperators,
    },
    {
        type: "text",
        id: "FullPhone",
        label: "FullPhone",
        operators: defaultOperators,
    },
    {
        type: "text",
        id: "NationalPhone",
        label: "NationalPhone",
        operators: defaultOperators,
    },
    {
        type: "text",
        id: "FirstName",
        label: "FirstName",
        operators: ["contains", "equal", "like"],
    },
    {
        type: "text",
        id: "LastName",
        label: "LastName",
        operators: ["contains", "equal", "like"],
    },
    {
        type: "text",
        id: "BirthDate",
        label: "BirthDate",
        operators: defaultOperators,
    },
    {
        type: "text",
        id: "Age",
        label: "Age",
        operators: defaultOperators,
    },
    {
        type: "select",
        id: "Gender",
        label: "Gender",
        choices: [
            {
                label: "Man",
                value: "M",
            },
            {
                label: "Famale",
                value: "F",
            },
        ],
    },
    {
        type: "text",
        id: "LastPullphoningStatus",
        label: "LastPullphoningStatus",
        operators: ["equal", "like"],
    },
    {
        type: "select",
        id: "ProviderCountryId",
        label: "ProviderCountry",
        choices: [
            {
                label: "Spain",
                value: "999",
            },
        ],
    },
    {
        type: "text",
        id: "Cp",
        label: "Cp",
        operators: ["equal", "like", "in"],
    },
];

export default {
    name: "CCSubQueryBuilder",
    mixins: [validationMixin, baseMixin],
    components: {
        VueQueryBuilder,
        CCSubQueryBuilderGroup,
        SelectAsync,
    },
    data() {
        return {
            valueComponent: {},

            defaultLabels: {
                matchType: "Match Type",
                matchTypes: [
                    { id: "all", label: "All" },
                    { id: "any", label: "Any" },
                ],
                limit: "Limit",
                addRule: "Add Rule",
                textSelectRulePlaceholder: "Select a field",
                removeRule: "&times;",
                addGroup: "Add Group",
                removeGroup: "&times;",
                textInputPlaceholder: "value",
            },

            fields: defaultRules.map((rule, index) => {
                return {
                    key: rule.id,
                    label: rule.label,
                    fixed: false,
                    _active: false,
                };
            }),

            // selectedField: null,

            editable: true,
            rules: defaultRules,

            defaultQuery: {
                logicalOperator: "all",
                children: [],
                fields: [],
                groupBy: [],
                limit: null,
            },

            query: {
                logicalOperator: "all",
                children: [],
                fields: [], //fields
                groupBy: [],
                limit: null,
            },

            _loading: false,
        };
    },
    props: {
        value: {
            type: Object|null,
            required: true,
        },
        loading: Boolean,
    },
    computed: {
        selectedField: {
            get() {
                return this.query.fields.length > 0
                    ? this.query.fields[0].key
                    : null;
            },
            set(newValue) {
                const findFieldIndex = this.groupableFields.findIndex((field) => {
                    return field.key == newValue;
                });
                if (findFieldIndex == -1) {
                    throw new Error("Field not found");
                }

                this.query.fields = [this.groupableFields[findFieldIndex]];
                this.query.groupBy = [this.groupableFields[findFieldIndex].key];
            },
        },

        groupableFields() {
            return this.fields.filter((field) => {
                return field.groupable;
            });
        },
    },
    watch: {
        value(newValue, oldValue) {
            // Esto es una guarrada y muy ineficiente
            if (JSON.stringify(newValue) != JSON.stringify(oldValue)) {
                this.query = this.getVueQueryBuilderFromQuery(newValue);
            }
        },
        query: {
            handler(newValue, oldValue) {
                const value = this.getQueryFromVueQueryBuilder(newValue);
                this.$emit("update:value", value);
                this.$emit("change", value);
            },
            deep: true,
        },
    },
    methods: {
        generateShortUniqueId,

        getQueryFromVueQueryBuilder(query) {
            const vueQueryBuilder = {
                type: query.type,
                logicalOperator: query.logicalOperator,
                children: query.children,
                fields: query.fields || [],
                limit: query.limit == "" ? undefined : query.limit,
                groupBy: query.groupBy || [],
            };
            const queryBuilderGroupType = "query-builder-group";
            const queryBuilderRuleType = "query-builder-rule";

            const queryBuilderTypes = {
                [queryBuilderGroupType]: "GroupCondition",
                [queryBuilderRuleType]: "Condition",
            };

            const logicalOperators = {
                all: "AND",
                any: "OR",
            };

            let whereQueryBuilder = [];
            for (const child of vueQueryBuilder.children) {
                if (child.type !== queryBuilderGroupType) {
                    whereQueryBuilder.push({
                        type: queryBuilderTypes[child.type],
                        field: child.query.rule,
                        condition: child.query.operator,
                        value: child.query.value,
                        logicalOperator:
                            logicalOperators[vueQueryBuilder.logicalOperator],
                    });
                } else if (child.type === queryBuilderGroupType) {
                    whereQueryBuilder.push({
                        ...this.getQueryFromVueQueryBuilder({
                            type: queryBuilderTypes[child.type],
                            ...child.query,
                        }),
                        logicalOperator:
                            logicalOperators[vueQueryBuilder.logicalOperator],
                    });
                } else {
                    throw new Error("Invalid query builder type");
                }
            }
            const fieldsQueryBuilder = vueQueryBuilder.fields.map((field) => {
                if (!field.key) {
                    throw new Error("Field invalid");
                }
                let mappingField = {
                    field: field.key,
                    alias: field.label,
                };
                if (field.type && field.type == "FixedField") {
                    mappingField = {
                        ...mappingField,
                        type: field.type,
                        value: field.value,
                    };
                }
                return mappingField;
            });

            return {
                type: vueQueryBuilder.type,
                where: whereQueryBuilder,
                fields: fieldsQueryBuilder,
                limit: vueQueryBuilder.limit,
                groupBy: vueQueryBuilder.groupBy,
            };
        },

        getVueQueryBuilderFromQuery(query) {
            if (!query) return this.defaultQuery;

            const types = {
                GroupCondition: "query-builder-group",
                Condition: "query-builder-rule",
            };

            const logicalOperators = {
                AND: "all",
                OR: "any",
            };

            const whereQueryBuilder = query.where ? query.where : [];
            const fieldsQueryBuilder = query.fields ? query.fields : [];
            const groupByQueryBuilder = query.groupBy ? query.groupBy : [];
            const firstLogicalOperador =
                whereQueryBuilder.length > 0
                    ? whereQueryBuilder[0].logicalOperator
                    : "AND"; // Tomamos por defecto la primera condición como el operador lógico.
            const vueQueryBuilder = {
                logicalOperator: logicalOperators[firstLogicalOperador],
                children: [],
                fields: [],
                groupBy: groupByQueryBuilder,
                limit: query.limit ? query.limit : null,
            };

            for (const where of whereQueryBuilder) {
                if (where.type === "Condition") {
                    vueQueryBuilder.children.push({
                        type: types[where.type],
                        query: {
                            rule: where.field,
                            operand: where.field,
                            value: where.value,
                            operator: where.condition,
                            logicalOperator: where.logicalOperator,
                        },
                    });
                } else if (where.type === "GroupCondition") {
                    vueQueryBuilder.children.push({
                        type: types[where.type],
                        // query: {
                        //     logicalOperator: where.logicalOperator,
                        //     children: [],
                        //     limit: where.limit,
                        // },
                        query: this.getVueQueryBuilderFromQuery(where),
                    });
                }
            }

            for (const field of fieldsQueryBuilder) {
                try {
                    const findField =
                        this.getVueQueryFieldFromQueryField(field);
                    vueQueryBuilder.fields.push(findField);
                } catch (error) {
                    // Si el campo no se encuentra verificamos que no sea un campo fijo
                    if (field.type && field.type == "FixedField") {
                        const _idRef = generateShortUniqueId();
                        vueQueryBuilder.fields.push({
                            type: field.type,
                            key: field.field,
                            label: field.alias ? field.alias : field.field,
                            value: field.value,
                            _idRef,
                        });
                    }
                }
            }
            return vueQueryBuilder;
        },

        getVueQueryFieldFromQueryField(field) {
            let vueQueryField = {};
            const findFieldIndex = this.fields.findIndex((item) => {
                if (field.field) {
                    return item.key == field.field;
                } else {
                    return item.key == field;
                }
            });

            if (findFieldIndex != -1) {
                this.fields[findFieldIndex]._active = true;
                vueQueryField = {
                    ...this.fields[findFieldIndex],
                    label: field.field
                        ? field.alias
                        : this.fields[findFieldIndex].label,
                    _idRef: generateShortUniqueId(),
                };
                return vueQueryField;
            } else {
                throw new Error("Field not found");
            }
        },

        getRulesFromQueryModel(queryModel) {
            let rules = [];
            const queryModelFields = queryModel.fields || [];
            
            for (const field of queryModelFields) {
                rules.push({
                    type: defaultRuleType,
                    id: field,
                    label: field,
                    groupable: queryModel.groupableFields
                        ? queryModel.groupableFields.includes(field)
                        : false,
                    operators: defaultOperators,
                });
            }
            return rules;
        },

        getFieldsFromRules(rules) {
            return rules.map((rule, index) => {
                return {
                    key: rule.id,
                    label: rule.label,
                    groupable: rule.groupable,
                    fixed: false,
                    _active: false,
                };
            });
        },

        async loadQueryModel() {
            const { data } = await this.fetcher({
                baseURL: process.env.VUE_APP_API_SASS,
                url: `/segmentations/query-models/${SEGMENTATIONS_DELIVERY_CAMPAINGS_DELIVERIES_QUERY_MODEL_ID}`,
                method: "GET",
            });
            const queryModel = {
                fields: data.fields,
                groupableFields: data.groupableFields,
            };
            this.rules = this.getRulesFromQueryModel(queryModel);
            this.fields = this.getFieldsFromRules(this.rules);
        },
    },

    async mounted() {
        this.query = this.getVueQueryBuilderFromQuery(this.value);
        await this.loadQueryModel();
    },
};
</script>

<style></style>
